import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { ShieldAlert, Crown, ArrowRight } from "lucide-react";

/**
 * Page-level access guard with plan-aware "Upgrade" experience.
 *
 * Two independent gates:
 *   • `module` — user-level RBAC (`user.allowed_modules`). Failure shows
 *     the "contact admin" panel.
 *   • `page`   — tenant-level plan membership (`effectivePages`). Failure
 *     shows the "Upgrade plan" panel. When omitted, defaults to `module`
 *     so existing routes (where the two ids match) keep working with no
 *     extra plumbing.
 */
export default function ModuleGuard({ module, page, children }) {
  const { canAccess, canAccessPage, effectivePages, user, loading } = useAuth();
  const { lang } = useLanguage();
  const isAr = lang === "ar";
  const pageId = page || module;

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;

  // Wait for the plan-page list to load before rendering the route's
  // children. Without this short wait, the protected page mounts and
  // fires axios calls that the backend correctly rejects with 403 —
  // producing noisy errors and a brief flash of the UI. Skipped for
  // super-admin (effectivePages stays null but admin sees everything).
  if (effectivePages === null && user.role !== "admin") {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-stone-400 text-sm" data-testid="plan-check-loading">
        <span className="inline-block w-4 h-4 border-2 border-stone-300 border-t-amber-500 rounded-full animate-spin me-2"/>
        {isAr ? "جارٍ التحقق من الخطة..." : "Checking plan..."}
      </div>
    );
  }

  // (2) RBAC — user-level restriction.
  if (!canAccess(module)) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6">
        <div className="card-surface max-w-md w-full p-8 text-center" data-testid="module-access-denied">
          <div className="mx-auto w-14 h-14 rounded-full bg-rose-500/10 flex items-center justify-center mb-4">
            <ShieldAlert className="text-rose-400" size={28} />
          </div>
          <h2 className="text-lg font-semibold text-[var(--text)] mb-2">
            {isAr ? "لا تملك صلاحية الوصول" : "Access Restricted"}
          </h2>
          <p className="text-sm text-[var(--text-muted)] mb-6">
            {isAr
              ? "هذه الصفحة غير متاحة لحسابك. تواصل مع المسؤول لمنحك الصلاحية."
              : "Your account does not have access to this page. Please contact your administrator."}
          </p>
          <a href="/" className="btn-gold inline-block">
            {isAr ? "العودة للوحة التحكم" : "Back to Dashboard"}
          </a>
        </div>
      </div>
    );
  }

  // (3) Plan — tenant-level restriction. Once the effective list has loaded
  // and the requested page is not in it, render the "Upgrade plan" panel.
  if (effectivePages !== null && !canAccessPage(pageId)) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6">
        <div className="card-surface max-w-lg w-full p-10 text-center relative overflow-hidden" data-testid="plan-page-block">
          <div className="absolute -end-6 -top-6 opacity-[0.07] pointer-events-none">
            <Crown size={160} className="text-amber-500" />
          </div>

          <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 text-white flex items-center justify-center shadow-lg mb-5 relative">
            <Crown size={28} />
          </div>

          <h2 className="font-heading text-2xl gold-text mb-2 relative">
            {isAr ? "هذه الصفحة لا تشملها خطتك" : "This page isn't in your plan"}
          </h2>
          <p className="text-sm text-[var(--text-muted)] mb-6 leading-relaxed relative">
            {isAr
              ? "للوصول إلى هذه الصفحة، يرجى ترقية اشتراكك إلى باقة أعلى. تواصل مع مالك المنصة لإجراء الترقية."
              : "To access this page, upgrade your subscription to a higher tier. Contact the platform owner to upgrade."}
          </p>
          <div className="inline-flex items-center gap-2 text-[11px] px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 mb-6 relative">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
            {isAr ? "الصفحة المطلوبة" : "Requested page"}: <span className="font-mono">{pageId}</span>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 justify-center relative">
            <a href="/" className="btn-gold inline-flex items-center justify-center gap-2">
              {isAr ? "العودة للوحة التحكم" : "Back to Dashboard"}
              <ArrowRight size={14} className={isAr ? "rotate-180" : ""} />
            </a>
          </div>
        </div>
      </div>
    );
  }

  return children;
}
